/**
 * CheckInV2 data layer — every network call this page makes lives here.
 * The check-in backend is the Cloudflare Worker REST API (the one page in the
 * portal that predates the RPC convention); the token/calendar/Zoom machinery
 * lives there, so V2 keeps the worker and centralizes the calls instead.
 */

import { supabase } from '@/lib/supabase';
import { getCalendarEvents } from '@/lib/api/calendar-api';
import type {
  AppointmentNote,
  CheckInRow,
  ClientSearchResult,
  CreatedAppointment,
  NewAppointmentInput,
  Profile,
} from './types';
import { EXCLUDED_EVENT_TYPES } from './config';
import type { CalendarEvent } from '@/lib/api/calendar-api';

const API_BASE = import.meta.env.VITE_API_BASE_URL;

async function authHeaders(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('Not authenticated');
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = await authHeaders();
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { ...headers, ...(init.headers ?? {}) },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as {
      error?: string;
      message?: string;
      details?: string;
    };
    // The worker puts the real cause in `details` ("Failed to assign
    // consultant" alone told nobody anything)
    const summary =
      body.error ?? body.message ?? `Request failed (${response.status})`;
    throw new Error(
      body.details && body.details !== summary
        ? `${summary}: ${body.details}`
        : summary
    );
  }
  return response.json() as Promise<T>;
}

/** ONE list call for the board: all recent check-ins with their relations */
export async function fetchCheckIns(): Promise<CheckInRow[]> {
  const result = await request<{ checkIns?: CheckInRow[] }>(
    '/api/check-in/dashboard'
  );
  return result.checkIns ?? [];
}

/**
 * Calendar events for a date range, minus system events (LLC/birthday/closed)
 * and events whose Google sync has deleted them.
 */
export async function fetchEvents(
  startDate: string,
  endDate: string
): Promise<CalendarEvent[]> {
  const response = await getCalendarEvents(startDate, endDate);
  // A failed calendar call must surface as a board error, not an empty board
  if (!response.success) {
    throw new Error(response.error || 'Failed to load calendar events');
  }
  if (!response.events) return [];
  return response.events.filter(
    e =>
      e.sync_status !== 'deleted' &&
      !(EXCLUDED_EVENT_TYPES as readonly string[]).includes(e.event_type)
  );
}

export async function updateStatus(
  checkInId: string,
  status: string,
  consultantId?: string
): Promise<void> {
  await request(`/api/check-in/${checkInId}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status, assignedConsultantId: consultantId }),
  });
}

export async function rescheduleCheckIn(
  checkInId: string,
  date: string,
  time: string,
  duration: number
): Promise<void> {
  await request(`/api/check-in/${checkInId}/reschedule`, {
    method: 'PATCH',
    body: JSON.stringify({ date, time, duration }),
  });
}

export async function manualCheckIn(
  calendarEventId: string
): Promise<string | null> {
  const result = await request<{ checkIn?: { id?: string } }>(
    '/api/check-in/manual',
    {
      method: 'POST',
      body: JSON.stringify({ calendarEventId }),
    }
  );
  return result.checkIn?.id ?? null;
}

/**
 * Link the appointment's check-in to a client. The backend looks the
 * check-in up by its Google calendar event id (`calendarEventId`); sending
 * only the check-in row id always failed with a 400. The row id is still
 * sent alongside it for backends that accept either.
 */
export async function linkClient(
  calendarEventId: string,
  checkInId: string,
  clientId: string
): Promise<void> {
  await request('/api/check-in/link-client', {
    method: 'POST',
    body: JSON.stringify({ calendarEventId, checkInId, clientId }),
  });
}

export async function assignConsultant(
  googleEventId: string,
  consultantId: string
): Promise<void> {
  await request(`/api/calendar/${googleEventId}/assign-consultant`, {
    method: 'PATCH',
    body: JSON.stringify({ consultantId }),
  });
}

export async function cancelAppointment(googleEventId: string): Promise<void> {
  await request(`/api/calendar/${googleEventId}`, { method: 'DELETE' });
}

/**
 * Create an appointment. Still the links/generate endpoint — check-in rows
 * hang off check_in_links, and this is where calendar-event + Zoom creation
 * happen — but the minted URL goes nowhere since the client app was retired.
 */
export async function createAppointment(
  input: NewAppointmentInput
): Promise<CreatedAppointment> {
  const result = await request<{ link: { id: string; fullUrl: string } }>(
    '/api/check-in/links/generate',
    {
      method: 'POST',
      body: JSON.stringify({
        clientId: input.clientId,
        isNewClient: input.isNewClient,
        isWalkIn: input.isWalkIn,
        isClientDropOff: input.isClientDropOff,
        isEmail: false,
        isOnDemand: false,
        tiffanyOnly: input.tiffanyOnly,
        isZoom: input.isZoom,
        notes: input.notes,
        appointmentDate: input.appointmentDate,
        language: input.language,
        employeeId: input.employeeId,
        duration: input.duration,
        appointmentType: input.appointmentType,
        calendarEventId: null,
        meetingProvider: input.isZoom ? 'zoom' : null,
        zoomSettings: input.isZoom
          ? {
              topic: 'TIFPA Consulting Appointment',
              waitingRoom: true,
              joinBeforeHost: false,
              participantVideo: true,
            }
          : null,
      }),
    }
  );
  return { linkId: result.link.id, linkUrl: result.link.fullUrl };
}

export async function fetchNotes(
  googleEventId: string
): Promise<AppointmentNote[]> {
  const result = await request<{ notes?: AppointmentNote[] }>(
    `/api/calendar/${googleEventId}/notes`
  );
  return result.notes ?? [];
}

export async function addNote(
  googleEventId: string,
  text: string
): Promise<void> {
  await request(`/api/calendar/${googleEventId}/notes`, {
    method: 'POST',
    body: JSON.stringify({ text: text.trim() }),
  });
}

/** Client search for the New Appointment drawer (V1's lean RPC, reused as-is) */
export async function searchClients(
  term: string
): Promise<ClientSearchResult[]> {
  const { data, error } = await supabase.rpc('search_clients_lean', {
    search_term: term,
    include_inactive: false,
    limit_count: 25,
    offset_count: 0,
  });
  if (error) throw new Error(`Client search failed: ${error.message}`);
  return ((data ?? []) as Array<Record<string, unknown>>).map(c => ({
    id: String(c.id),
    name: String(c.name ?? c.legal_name ?? ''),
    email: (c.email as string | null) ?? null,
    phone_number: (c.phone_number as string | null) ?? null,
  }));
}

/**
 * Consultant picker options. Scoped to the staff roster emails — the legacy
 * hook fetched EVERY profile row and filtered client-side (the 2026-08-06
 * S3 finding); this pushes the filter into the query. Matching is
 * case-insensitive on both sides: `.in('email', …)` is exact-match in
 * Postgres, so a stored 'Tiffany@…' would silently vanish and take the whole
 * consultant dropdown with it — the ilike-based .or() below tolerates any
 * casing in the profiles table.
 */
export async function fetchProfilesByEmails(
  emails: string[]
): Promise<Profile[]> {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, name, display_name, email')
    .or(emails.map(e => `email.ilike.${e}`).join(','));
  if (error) throw new Error(`Profile lookup failed: ${error.message}`);
  return ((data ?? []) as Array<Record<string, unknown>>).map(p => ({
    id: String(p.id),
    name:
      (p.display_name as string | null) ??
      (p.name as string | null) ??
      String(p.email ?? '').split('@')[0],
    email: String(p.email ?? '').toLowerCase(),
  }));
}

/**
 * Realtime: one channel for the whole page. Any change to check-ins or the
 * calendar sync log triggers the (debounced, silent) reload the hook owns.
 */
export function subscribeToBoard(onChange: () => void): () => void {
  const channel = supabase
    .channel('check-in-v2-board')
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'client_check_ins' },
      onChange
    )
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'calendar_sync_log' },
      onChange
    )
    .subscribe();
  return () => {
    supabase.removeChannel(channel);
  };
}
